import React, { useEffect, useRef, useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import { server } from "../../server";
import { loadSellers, setUser } from "../../redux/actions/user";
import {
  cacheAuthUser,
  clearAuthSession,
  persistAuthToken,
} from "../../utils/authSession";
import { setStoredCurrentProfile } from "../../utils/shopProfile";
import { isPrivateLaunchSuperAdmin } from "../../utils/privateLaunchAccess";
import { getCurrentHostname } from "../../utils/publicApp";
import churchLogo from "../../assets/church-login/matrix-avatar.png";

const CHURCH_STATIC_BACKGROUND_SRC = "https://www.churchofmatrix.org/assets/images/church-of-matrix-bkg.png";
const MATRIX_BACKGROUND_SCRIPT_ID = "private-launch-matrix-background-script";
const MATRIX_BACKGROUND_SCRIPT_SRC = "/assets/church-login/matrix-background.js";
const MATRIX_BACKGROUND_CONFIG_SRC = "/assets/church-login/matrix-scene.json";
const STATIC_BACKGROUND_MEDIA_QUERY = "(max-width: 768px), (pointer: coarse), (prefers-reduced-motion: reduce)";

const shouldPreferStaticBackground = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(STATIC_BACKGROUND_MEDIA_QUERY).matches;
};

const getSafeRedirectPath = () => {
  if (typeof window === "undefined") {
    return "/home";
  }

  const currentPath = String(window.location.pathname || "/").trim();
  const currentSearch = new URLSearchParams(window.location.search || "");
  const requestedRedirect = String(currentSearch.get("redirect") || "").trim();

  if (requestedRedirect.startsWith("/") && !requestedRedirect.startsWith("//")) {
    return requestedRedirect;
  }

  if (currentPath.startsWith("/profile-entry/")) {
    return currentPath;
  }

  if (currentPath && currentPath !== "/" && currentPath !== "/login") {
    return currentPath;
  }

  return "/home";
};

const ensureMatrixBackgroundScript = () => {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (window.MatrixDataCityBackground) {
    return Promise.resolve(window.MatrixDataCityBackground);
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(MATRIX_BACKGROUND_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.MatrixDataCityBackground || null), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load matrix background script.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = MATRIX_BACKGROUND_SCRIPT_ID;
    script.src = MATRIX_BACKGROUND_SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", () => resolve(window.MatrixDataCityBackground || null), { once: true });
    script.addEventListener("error", () => reject(new Error("Failed to load matrix background script.")), { once: true });
    document.body.appendChild(script);
  });
};

const PrivateLaunchLogin = () => {
  const dispatch = useDispatch();
  const matrixCanvasRef = useRef(null);
  const [identifier, setIdentifier] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return String(new URLSearchParams(window.location.search || "").get("identifier") || "");
  });
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prefersStaticBackground, setPrefersStaticBackground] = useState(() => shouldPreferStaticBackground());
  const currentHostname = getCurrentHostname();
  const useChurchTheme = true;
  const forgotPasswordHref = "/forgot-password";
  const forgotUsernameHref = "/forgot-username";
  const useStaticBackground = useChurchTheme && prefersStaticBackground;
  const shouldAnimateBackground = useChurchTheme && !useStaticBackground;
  const loginScreenStyle = useStaticBackground
    ? {
        backgroundImage: `linear-gradient(180deg, rgba(1, 4, 5, 0.48), rgba(1, 2, 2, 0.72)), url(${CHURCH_STATIC_BACKGROUND_SRC})`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }
    : undefined;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(STATIC_BACKGROUND_MEDIA_QUERY);
    const handleChange = (event) => {
      setPrefersStaticBackground(event.matches);
    };

    setPrefersStaticBackground(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleChange);
      return () => mediaQueryList.removeEventListener("change", handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, []);

  useEffect(() => {
    let instance;
    let disposed = false;

    const startMatrixBackground = async () => {
      try {
        await ensureMatrixBackgroundScript();

        if (disposed || !matrixCanvasRef.current || !window.MatrixDataCityBackground) {
          return;
        }

        instance = await window.MatrixDataCityBackground.init({
          canvas: matrixCanvasRef.current,
          configUrl: MATRIX_BACKGROUND_CONFIG_SRC,
        });

        if (disposed && instance) {
          instance.destroy();
        }
      } catch (error) {
        console.warn("Matrix login background failed to initialize.", error);
      }
    };

    if (shouldAnimateBackground && currentHostname) {
      startMatrixBackground();
    }

    return () => {
      disposed = true;
      if (instance) {
        instance.destroy();
      }
    };
  }, [currentHostname, shouldAnimateBackground]);

  const clearRejectedLogin = async () => {
    clearAuthSession();
    setStoredCurrentProfile("User");

    try {
      await axios.post(`${server}/user/logout`, {}, { withCredentials: true });
    } catch (error) {
      // The local session has already been cleared; a logout network miss should not reopen access.
    }

    dispatch({
      type: "LoadUserFail",
      payload: {
        message: "Private launch access is limited.",
        isAuthFailure: true,
      },
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${server}/user/login-user`,
        { identifier, password },
        { withCredentials: true }
      );

      const { user, token } = response.data || {};

      if (!isPrivateLaunchSuperAdmin(user)) {
        await clearRejectedLogin();
        toast.error("Private launch is limited to the platform owner right now.");
        return;
      }

      dispatch(setUser(user));
      cacheAuthUser(user);
      persistAuthToken(token || user?.token);
      setStoredCurrentProfile("User");
      await dispatch(loadSellers());
      toast.success("Login Success!");
      window.location.assign(getSafeRedirectPath());
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Login failed. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`private-launch-login-screen${useChurchTheme ? " private-launch-login-screen--church" : ""}${useStaticBackground ? " private-launch-login-screen--static" : ""}`}
      style={loginScreenStyle}
    >
      {shouldAnimateBackground ? <canvas ref={matrixCanvasRef} className="private-launch-login-matrix-bg" aria-hidden="true" /> : null}
      {useChurchTheme ? <div className="private-launch-login-overlay" aria-hidden="true" /> : null}
      <main className="private-launch-login-shell" aria-labelledby="private-launch-login-title">
        <section className="private-launch-login-panel">
          <div className="private-launch-login-content">
            <div className="private-launch-login-brand">
              <div className="private-launch-login-logo-wrap">
                <img src={churchLogo} alt="Church of Matrix logo" className="private-launch-login-logo" />
              </div>

              <div className="private-launch-login-heading-block">
                <h1 id="private-launch-login-title" className="private-launch-login-title">
                  MATRIX
                </h1>
                <p className="private-launch-login-subtitle">
                  Sign in to your Matrix.
                </p>
              </div>
            </div>

            <form className="private-launch-login-form" onSubmit={handleSubmit}>
              <label className="private-launch-login-label" htmlFor="private-launch-identifier">
                Email or Handle
              </label>
              <input
                id="private-launch-identifier"
                type="text"
                name="identifier"
                autoComplete="username"
                required
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                className="private-launch-login-input"
              />

              <label className="private-launch-login-label" htmlFor="private-launch-password">
                Password
              </label>
              <div className="private-launch-login-password-wrap">
                <input
                  id="private-launch-password"
                  type={visible ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="private-launch-login-input private-launch-login-password-input"
                />
                <button
                  type="button"
                  className="private-launch-login-eye-button"
                  onClick={() => setVisible((nextVisible) => !nextVisible)}
                  aria-label={visible ? "Hide password" : "Show password"}
                >
                  {visible ? <AiOutlineEye size={22} /> : <AiOutlineEyeInvisible size={22} />}
                </button>
              </div>

              <button
                type="submit"
                className="private-launch-login-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Signing In" : "Sign In"}
              </button>

              <div className="private-launch-login-support">
                <a href={forgotPasswordHref} className="private-launch-login-support-link">
                  Forgot your password?
                </a>
                <a href={forgotUsernameHref} className="private-launch-login-support-link">
                  Forgot your handle?
                </a>
              </div>
            </form>
          </div>

        </section>
      </main>
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default PrivateLaunchLogin;